import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import MedicalForm from './Form'; // Imported Form component
import { useNavigate } from 'react-router-dom'; // Add this import

// Styled components
const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #F8FCFF;
`;

const FormContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  gap: 24px;
  width: 450px;
  border: 1px solid #E2E2E3;
  border-radius: 24px;
  background-color: white;
`;

const Logo = styled.img`
  width: 380px;
  height: 180px;
`;

const Title = styled.h1`
  font-family: 'Sofia Sans', sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: #2C2C2D;
  margin: 16px 0;
`;

// Styled Form
const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-family: 'Sofia Sans', sans-serif;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;
  display: flex;
  align-items: center;
`;

const RequiredStar = styled.span`
  color: #E43434;
  margin-right: 4px;
`;

const Input = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 56px;
  padding: 18px 16px;
  background: #FFFFFF;
  border: 1px solid ${props => props.error ? '#E43434' : '#A7A7AA'};
  border-radius: 12px;
  font-family: 'Sofia Sans', sans-serif;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;

  &::placeholder {
    color: #A7A7AA;
  }

  &:focus {
    outline: none;
    border-color: #2575BD;
  }
`;

const HelperText = styled.span`
  width: 100%;
  font-family: 'Sofia Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  display: flex;
  align-items: center;
  color: #E43434;
`;

const Button = styled.button`
  width: 100%;
  height: 56px;
  background-color: ${props => props.disabled ? '#E0F0FF' : '#2575BD'};
  color: ${props => props.disabled ? '#91D3FF' : '#FFFFFF'};
  border: none;
  border-radius: 12px;
  font-family: 'Sofia Sans', sans-serif;
  font-size: 18px;
  line-height: 21px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.3s;
  margin-top: 16px;

  &:hover {
    background-color: ${props => props.disabled ? '#E0F0FF' : '#1c5c94'};
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledCheckbox = styled.input`
  margin-right: 8px;
`;

const CheckboxLabel = styled.label`
  font-family: 'Sofia Sans', sans-serif;
  font-size: 14px;
  line-height: 17px;
  color: #2C2C2D;
`;

const FileUploadWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  gap: 10px;
  width: 100%;
  height: 56px;
  background: #FFFFFF;
  border: 1px solid #F0F0F0;
  border-radius: 12px;
  margin-top: 8px;
`;

const FileUploadText = styled.span`
  font-family: 'Sofia Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #6D6D71;
`;

const FileUploadIconWrapper = styled.label`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 8px;
  width: 32px;
  height: 32px;
  border: 1px solid #A7A7AA;
  border-radius: 4px;
  cursor: pointer;
`;

const FileUploadIcon = styled.div`
  width: 16px;
  height: 16px;
  border: solid 2px #6D6D71;
  border-top: none;
  border-left: none;
  transform: rotate(45deg);
  margin-top: -4px;
`;

const FileInput = styled.input`
  display: none;
`;

const ErrorMessage = styled.div`
  color: #E43434;
  font-family: 'Sofia Sans', sans-serif;
  font-size: 14px;
  margin-top: 8px;
`;

const LandingPage = () => {
  const [participantId, setParticipantId] = useState(''); // Example ID
  const [formData, setFormData] = useState({
    participantId: '',
    consent: false,
    pdfFile: null,
  });
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [submitted, setSubmitted] = useState(false); // State to manage form submission
  const [backendError, setBackendError] = useState(''); // State to hold backend error message
  const navigate = useNavigate(); // Add this line

  useEffect(() => {
    setIsFormValid(
      formData.participantId &&
      formData.consent &&
      Object.keys(errors).length === 0
    );
  }, [formData, errors]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value
    }));
    validateField(name, type === 'checkbox' ? checked : type === 'file' ? files[0] : value);
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };

    switch (name) {
      case 'participantId':
        if (!value) {
          newErrors.participantId = 'Participant ID is required';
        } else {
          delete newErrors.participantId;
        }
        break;
      case 'consent':
        if (!value) {
          newErrors.consent = 'Consent is required';
        } else {
          delete newErrors.consent;
        }
        break;
      case 'pdfFile':
        if (value && value.type !== 'application/pdf') {
          newErrors.pdfFile = 'Please upload a PDF file';
        } else if (value && value.size > 2 * 1024 * 1024) {
          newErrors.pdfFile = 'File size exceeds 2 MB limit';
        } else {
          delete newErrors.pdfFile;
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isFormValid) {
      const payload = {
        participant_id: formData.participantId,
        flags: {
          "info": false,
          "clinical": false,
          "general": false,
          "cardiac": false,
          "ecg": false,
          "echocardiogram": false,
          "consent_file": false,
          "ecg_file": false,
          "echocardiogram_file": false,
          "phonocardiogram_file": false,
          "lightscope_file": false
        },
      };

      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}participant/create`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // On successful response, navigate to the MedicalForm
        const { participant_id } = payload.participant_id;
        navigate(`/form/${payload.participant_id}`);
        setBackendError(''); // Clear any previous backend error
      } catch (error) {
        console.error('Error submitting form:', error);
        // Check if the error response has an error message
        if (error.response && error.response.data && error.response.data.error) {
          // Set backend error message from the response
          setBackendError(error.response.data.error);
        } else {
          // Fallback error message
          setBackendError('An unexpected error occurred. Please try again later.');
        }
      }
    }
  };

  return (
    <PageContainer>
      <FormContainer>
        <Logo src="/logo-ai.png" alt="Heart Logo" />
        <Title>Medical Examination Study</Title>
        <StyledForm onSubmit={handleSubmit}>
          <InputWrapper>
            <Label htmlFor="participantId">
              <RequiredStar>*</RequiredStar>
              Participant ID
            </Label>
            <Input
              type="text"
              id="participantId"
              name="participantId"
              value={formData.participantId}
              onChange={handleChange}
              error={errors.participantId}
              placeholder="Enter Participant ID"
            />
            {errors.participantId && <HelperText>{errors.participantId}</HelperText>}
          </InputWrapper>

          <CheckboxWrapper>
            <StyledCheckbox
              type="checkbox"
              id="consent"
              name="consent"
              checked={formData.consent}
              onChange={handleChange}
            />
            <CheckboxLabel htmlFor="consent">
              I give consent to participate in the study
            </CheckboxLabel>
          </CheckboxWrapper>
          {errors.consent && <HelperText>{errors.consent}</HelperText>}

          {/* <InputWrapper>
            <Label htmlFor="pdfFile">
              Upload PDF <RequiredStar>*</RequiredStar>
            </Label>
            <FileUploadWrapper>
              <FileUploadText>{formData.pdfFile ? formData.pdfFile.name : 'Choose PDF'}</FileUploadText>
              <FileUploadIconWrapper>
                <FileUploadIcon />
              </FileUploadIconWrapper>
              <FileInput
                type="file"
                id="pdfFile"
                name="pdfFile"
                accept="application/pdf"
                onChange={handleChange}
              />
            </FileUploadWrapper>
            {errors.pdfFile && <HelperText>{errors.pdfFile}</HelperText>}
          </InputWrapper> */}

          {backendError && <ErrorMessage>{backendError}</ErrorMessage>} {/* Display backend error message */}

          <Button type="submit" disabled={!isFormValid}>
            Start Medical Examination
          </Button>
        </StyledForm>
      </FormContainer>
    </PageContainer>
  );
};

export default LandingPage;
